// QuestionChoices.jsx

import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Table, Form, Button, Modal, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import {
  getChoicesByQuestionId,
  addChoice,
  updateChoice,
  deleteChoice,
} from '../services/choiceService';
import { getAllQuestions } from '../services/questionService';

const QuestionChoices = () => {
  const { id: questionId } = useParams();
  const navigate = useNavigate();

  const [choices, setChoices] = useState([]);
  const [questionText, setQuestionText] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [addingChoice, setAddingChoice] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [editDescription, setEditDescription] = useState('');
  const [updatingId, setUpdatingId] = useState(null);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteCandidate, setDeleteCandidate] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const fetchChoices = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await getChoicesByQuestionId(questionId);
      setChoices(data.Data.Items);
    } catch (error) {
      console.error('Seçenekler yüklenirken hata:', error);
      toast.error('Seçenekler alınamadı.');
    } finally {
      setLoading(false);
    }
  }, [questionId]);

  const fetchQuestionText = useCallback(async () => {
    try {
      const questions = await getAllQuestions();
      const current = questions.find((q) => q.id === questionId);
      setQuestionText(current?.text || '');
    } catch (error) {
      console.error('Soru metni yüklenirken hata:', error);
      setQuestionText('');
    }
  }, [questionId]);

  useEffect(() => {
    fetchChoices();
    fetchQuestionText();
  }, [fetchChoices, fetchQuestionText]);

  const handleAdd = async () => {
    if (!newDescription.trim()) {
      toast.warning('Seçenek açıklaması boş olamaz.');
      return;
    }
    
    try {
      setAddingChoice(true);
      const res = await addChoice({ questionId, description: newDescription.trim() });
    
      if (!res.Status) {
        toast.error(res.Message || 'Seçenek eklenirken hata oluştu.');
        return;
      }
      
      toast.success(res.Message || 'Seçenek başarıyla eklendi.');
      setNewDescription('');
      
      // Listeyi yenile
      await fetchChoices();
      
    } catch (error) {
      console.error('Ekleme hatası:', error);
      toast.error('Ekleme sırasında hata oluştu.');
    } finally {
      setAddingChoice(false);
    }
  };

  const handleUpdate = async (id) => {
    if (!editDescription.trim()) {
      toast.warning('Seçenek açıklaması boş olamaz.');
      return;
    }

    try {
      setUpdatingId(id);
      const res = await updateChoice({ 
        id, 
        questionId, 
        description: editDescription.trim() 
      });
      
      if (!res.Status) {
        toast.error(res.Message || 'Seçenek güncellenirken hata oluştu.');
        return;
      }

      toast.success(res.Message || 'Seçenek başarıyla güncellendi.');
      setEditingId(null);
      setEditDescription('');
      
      // Listeyi yenile
      await fetchChoices();
      
    } catch (error) {
      console.error('Güncelleme hatası:', error);
      toast.error('Güncelleme sırasında hata oluştu.');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteCandidate) return;

    try {
      setDeletingId(deleteCandidate.id);
      const res = await deleteChoice(deleteCandidate.id);
      if (!res.Status) {
        toast.error(res.Message || 'Seçenek silinirken hata oluştu.');
        return;
      }

      toast.success(res.Message || 'Seçenek başarıyla silindi.');
      setShowDeleteModal(false);
      setDeleteCandidate(null);
      
      // Listeyi yenile
      await fetchChoices();
      
    } catch (error) {
      console.error('Silme hatası:', error);
      toast.error('Silme sırasında hata oluştu.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditDescription('');
  };

  const handleStartEdit = (choice) => {
    setEditingId(choice.id);
    setEditDescription(choice.description);
  };

  const handleKeyPress = (e, action, ...args) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      action(...args);
    }
  };

  return (
    <Card>
      <Card.Header className="d-flex justify-content-between align-items-center">
        <h5 className="mb-0">{questionText} - Seçenekler</h5>
        <Button variant="secondary" size="sm" onClick={() => navigate(-1)}>
          ← Geri
        </Button>
      </Card.Header>
      <Card.Body>
        {/* Yeni Seçenek Ekleme Alanı */}
        <div className="mb-4 row g-2">
          <div className="col-md-10">
            <Form.Control
              placeholder="Yeni Seçenek Açıklaması"
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              onKeyPress={(e) => handleKeyPress(e, handleAdd)}
              disabled={addingChoice}
            />
          </div>
          <div className="col-md-2">
            <Button 
              className="w-100" 
              onClick={handleAdd} 
              variant="success"
              disabled={addingChoice || !newDescription.trim()}
            >
              {addingChoice ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Ekleniyor...
                </>
              ) : (
                'Ekle'
              )}
            </Button>
          </div>
        </div>

        {/* Seçenekler Tablosu */}
        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Yükleniyor...</span>
            </Spinner>
          </div>
        ) : (
          <Table bordered hover responsive>
            <thead className="table-light">
              <tr>
                <th style={{ width: '10%' }}>ID</th>
                <th style={{ width: '60%' }}>Açıklama</th>
                <th style={{ width: '30%' }}>İşlem</th>
              </tr>
            </thead>
            <tbody>
              {choices.length === 0 ? (
                <tr>
                  <td colSpan="3" className="text-center text-muted py-4">
                    Henüz seçenek eklenmemiş.
                  </td>
                </tr>
              ) : (
                choices.map((c) => (
                  <tr key={c.id}>
                    <td>{c.id}</td>
                    <td>
                      {editingId === c.id ? (
                        <Form.Control
                          value={editDescription}
                          onChange={(e) => setEditDescription(e.target.value)}
                          onKeyPress={(e) => handleKeyPress(e, handleUpdate, c.id)}
                          disabled={updatingId === c.id}
                          autoFocus
                        />
                      ) : (
                        <span>{c.description}</span>
                      )}
                    </td>
                    <td>
                      {editingId === c.id ? (
                        <div className="d-flex gap-2">
                          <Button
                            size="sm"
                            variant="primary"
                            onClick={() => handleUpdate(c.id)}
                            disabled={updatingId === c.id || !editDescription.trim()}
                          >
                            {updatingId === c.id ? (
                              <>
                                <Spinner size="sm" className="me-1" />
                                Kaydediliyor...
                              </>
                            ) : (
                              'Kaydet'
                            )}
                          </Button>
                          <Button 
                            size="sm" 
                            variant="secondary"
                            onClick={handleCancelEdit}
                            disabled={updatingId === c.id}
                          >
                            İptal
                          </Button>
                        </div>
                      ) : (
                        <div className="d-flex gap-2">
                          <Button
                            size="sm"
                            variant="warning"
                            onClick={() => handleStartEdit(c)}
                            disabled={editingId !== null}
                          >
                            Düzenle
                          </Button>
                          <Button
                            size="sm"
                            variant="danger"
                            onClick={() => {
                              setDeleteCandidate(c);
                              setShowDeleteModal(true);
                            }}
                            disabled={editingId !== null}
                          >
                            Sil
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        )}

        {/* Silme Onay Modalı */}
        <Modal 
          show={showDeleteModal} 
          onHide={() => setShowDeleteModal(false)} 
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Seçenek Sil</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="mb-0">
              <strong>"{deleteCandidate?.description}"</strong> adlı seçeneği silmek istediğinizden emin misiniz?
            </p>
            <small className="text-muted">Bu işlem geri alınamaz.</small>
          </Modal.Body>
          <Modal.Footer>
            <Button 
              variant="secondary" 
              onClick={() => setShowDeleteModal(false)}
              disabled={deletingId}
            >
              Vazgeç
            </Button>
            <Button 
              variant="danger" 
              onClick={handleDelete}
              disabled={deletingId}
            >
              {deletingId ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Siliniyor...
                </>
              ) : (
                'Evet, Sil'
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      </Card.Body>
    </Card>
  );
};

export default QuestionChoices;